import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import blogData from '../components/blogData.json';
import { Calendar, Clock, Tag, ChevronRight, CatIcon, Github, FileText, Linkedin, Mail, ExternalLink, Menu, X, User, Briefcase, Send } from 'lucide-react';
import LoadingScreen from '../components/LoadingScreen';
const Blog = () => {
  const [blogs, setBlogs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showNavbar, setShowNavbar] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const navigate = useNavigate();
  const revealRef = useScrollReveal(0.1);
  const [isLoading, setIsLoading] = useState(true);
  const navLinks = [
    { name: "About", href: "/#about", icon: <User size={18} /> },
    { name: "Skills", href: "/#skills", icon: <FileText size={18} /> },
    { name: "Projects", href: "/#projects", icon: <Briefcase size={18} /> },
    { name: "Blog", href: "/blog", icon: <FileText size={18} /> },
  ];
  useEffect(() => {
    // Simulate loading time or wait for resources
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 2000); // Adjust timing as needed (2 seconds in this example)
    
    return () => clearTimeout(timer);
  }, []);
  useEffect(() => {
    // In a real application, you might fetch this data from an API
    setBlogs(blogData);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setShowNavbar(false);

        if (isMenuOpen) {
          setIsMenuOpen(false);
        }
      } else {
        setShowNavbar(true);
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [lastScrollY, isMenuOpen]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  // Filter blogs based on search term and selected tag
  const filteredBlogs = blogs.filter(blog => {
    const matchesSearch = blog.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          blog.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesTag = selectedTag ? blog.tags.includes(selectedTag) : true;
    return matchesSearch && matchesTag;
  });

  // Get all unique tags from blogs
  const allTags = [...new Set(blogs.flatMap(blog => blog.tags))];

  return (
    <>
    {isLoading ? (
      <LoadingScreen />
    ) : (
    <div className="min-h-screen bg-gray-900 text-gray-100">
      {/* Add CSS styles */}
      <style jsx global>{`
        .animate-reveal {
          animation: reveal 0.8s ease forwards;
        }

        @keyframes reveal {
          0% {
            opacity: 0;
            transform: translateY(30px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .reveal-element {
          opacity: 0;
          transform: translateY(30px);
        }

        .staggered-reveal > * {
          opacity: 0;
          transform: translateY(20px);
          transition: opacity 0.5s ease, transform 0.5s ease;
        }

        .staggered-reveal > *:nth-child(1) { transition-delay: 0.1s; }
        .staggered-reveal > *:nth-child(2) { transition-delay: 0.2s; }
        .staggered-reveal > *:nth-child(3) { transition-delay: 0.3s; }
        .staggered-reveal > *:nth-child(4) { transition-delay: 0.4s; }
        .staggered-reveal > *:nth-child(5) { transition-delay: 0.5s; }

        .animate-reveal.staggered-reveal > * {
          opacity: 1;
          transform: translateY(0);
        }

        .nav-link {
          position: relative;
          transition: all 0.3s ease;
        }

        .nav-link::after {
          content: '';
          position: absolute;
          width: 0;
          height: 2px;
          bottom: -4px;
          left: 0;
          background: linear-gradient(to right, #2dd4bf, #a855f7);
          transition: width 0.3s ease;
        }

        .nav-link:hover::after {
          width: 100%;
        }
      `}</style>

      {/* Navigation Bar - Copied from homepage */}
      <nav className={`fixed w-full z-10 bg-gray-900 bg-opacity-95 border-b border-gray-800 shadow-lg transition-transform duration-300 ${showNavbar ? 'translate-y-0' : '-translate-y-full'}`}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <CatIcon size={20} className="text-cyan-400 mr-2" />
              <span className="font-bold text-xl text-cyan-400">ARXHR<span className="text-purple-400">007</span></span>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              {navLinks.map((link) => (
                <a 
                  key={link.name}
                  href={link.href} 
                  className="nav-link flex items-center space-x-2 text-gray-300 hover:text-cyan-400 transition-colors duration-300"
                >
                  {link.icon}
                  <span>{link.name}</span>
                </a>
              ))}
              <a 
                href="/#contact" 
                className="bg-gradient-to-r from-cyan-500 to-purple-500 hover:from-cyan-600 hover:to-purple-600 text-white px-5 py-2 rounded-md flex items-center space-x-2 transition-all duration-300 shadow-lg hover:shadow-cyan-500/20"
              >
                <Send size={18} />
                <span>Contact</span>
              </a>
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden flex items-center">
              <button onClick={toggleMenu} className="text-gray-300 hover:text-white focus:outline-none">
                {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>

        <div 
          className={`md:hidden bg-gray-800 transition-all duration-300 ease-in-out overflow-hidden ${
            isMenuOpen ? 'max-h-70 opacity-100 visible' : 'max-h-0 opacity-0 invisible'
          }`}
        >
          <div className="px-4 pt-2 pb-3">
            {navLinks.map((link) => (
              <a 
                key={link.name}
                href={link.href} 
                className="flex items-center space-x-3 py-3 px-2 hover:bg-gray-700 rounded-md text-gray-300 hover:text-cyan-400 transition-all duration-200" 
                onClick={toggleMenu}
              >
                {link.icon}
                <span>{link.name}</span>
              </a>
            ))}
            <a 
              href="/#contact" 
              className="flex items-center space-x-3 py-3 px-2 mt-2 bg-gradient-to-r from-cyan-500 to-purple-500 rounded-md text-white transition-all duration-200"
              onClick={toggleMenu}
            >
              <Send size={18} />
              <span>Contact</span>
            </a>
          </div>
        </div>
      </nav>

      {/* Mobile menu spacing */}
      <div className={`md:hidden transition-all duration-300 ease-in-out ${isMenuOpen ? 'h-45 md:h-0' : 'h-0'}`}></div>

      {/* Blog Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 pt-28">
        {/* Header */}
        <div ref={revealRef} className="text-center mb-12 reveal-element">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-cyan-400 to-purple-500 text-transparent bg-clip-text">
            Blog & Articles
          </h1>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto">
            Insights on web development, cybersecurity, and tech innovations
          </p>
        </div>

        {/* Search and Filter */}
        <div className="mb-10 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="col-span-2">
            <input
              type="text"
              placeholder="Search articles..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-gray-100 focus:outline-none focus:ring-2 focus:ring-cyan-500"
            />
          </div>
          <div>
            <select
              value={selectedTag}
              onChange={(e) => setSelectedTag(e.target.value)}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-gray-100 focus:outline-none focus:ring-2 focus:ring-cyan-500"
            >
              <option value="">All Categories</option>
              {allTags.map(tag => (
                <option key={tag} value={tag}>{tag}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Blog Posts Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredBlogs.map((blog) => (
            <div
              key={blog.id}
              ref={revealRef}
              className="bg-gray-800 rounded-lg overflow-hidden shadow-lg border border-gray-700 hover:border-cyan-500 transition-all duration-300 flex flex-col h-full reveal-element"
            >
              <div className="h-48 overflow-hidden">
                <img
                  src={blog.imageUrl}
                  alt={blog.title}
                  className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                />
              </div>
              <div className="p-6 flex-grow">
                <div className="flex gap-4 mb-3">
                  <span className="text-sm text-gray-400 flex items-center">
                    <Calendar size={14} className="mr-1 text-cyan-400" />
                    {blog.date}
                  </span>
                  <span className="text-sm text-gray-400 flex items-center">
                    <Clock size={14} className="mr-1 text-purple-400" />
                    {blog.readTime} min read
                  </span>
                </div>
                <h3 className="text-xl font-semibold mb-2 hover:text-cyan-400 transition-colors duration-300">
                  {blog.title}
                </h3>
                <p className="text-gray-300 mb-4 line-clamp-3">{blog.description}</p>
                <div className="flex flex-wrap gap-2 mb-4">
                  {blog.tags.map(tag => (
                    <span key={tag} 
                      className="bg-gray-700 text-cyan-400 text-xs px-3 py-1 rounded-full flex items-center cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedTag(tag);
                      }}
                    >
                      <Tag size={12} className="mr-1" />
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
              <div className="px-6 pb-6">
                <button
                  onClick={() => navigate(`/blog/${blog.id}`)}
                  className="w-full bg-gradient-to-r from-cyan-500 to-purple-500 hover:from-cyan-600 hover:to-purple-600 text-white px-4 py-2 rounded-md flex items-center justify-center space-x-2 transition-all duration-300 shadow-lg hover:shadow-cyan-500/20"
                >
                  <span>Read Article</span>
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Empty State */}
        {filteredBlogs.length === 0 && (
          <div className="text-center py-16">
            <h3 className="text-xl font-semibold mb-2 text-gray-300">No articles found</h3>
            <p className="text-gray-400">Try adjusting your search or filter criteria</p>
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="py-8 border-t border-gray-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="mb-4 md:mb-0">
              <span className="font-bold text-xl text-cyan-400">ARXHR<span className="text-purple-400">007</span></span>
              <p className="text-gray-400 text-sm mt-1">Building secure and scalable web solutions</p>
            </div>
          </div>
          <div className="mt-8 text-center text-gray-500 text-sm">
            © {new Date().getFullYear()} Arxhr007. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
        )}
  </>
  );
};

// Hook for scroll reveal animations
const useScrollReveal = (threshold = 0.1) => {
  const [elements, setElements] = useState([]);
  const observer = useRef(null);

  useEffect(() => {
    observer.current = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-reveal');
          observer.current.unobserve(entry.target);
        }
      });
    }, { threshold });

    elements.forEach(el => {
      if (el) observer.current.observe(el);
    });

    return () => {
      if (observer.current) {
        observer.current.disconnect();
      }
    };
  }, [elements, threshold]);

  const ref = (el) => {
    if (el && !elements.includes(el)) {
      setElements(prev => [...prev, el]);
    }
  };

  return ref;
};

export default Blog;